import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { File, Paths } from 'expo-file-system';
import { Platform } from 'react-native';

// People files live in the private people S3 bucket and go through the people sign
// endpoints. The shared web util reads the JWT from localStorage, so the mobile app
// needs these AsyncStorage-based equivalents.

export const PEOPLE_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const PEOPLE_DOCUMENT_MIME_TYPE_BY_EXTENSION = {
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	jpeg: 'image/jpeg',
	jpg: 'image/jpeg',
	pdf: 'application/pdf',
	png: 'image/png',
	webp: 'image/webp',
};

export const PEOPLE_DOCUMENT_PICKER_MIME_TYPES = [
	...new Set(Object.values(PEOPLE_DOCUMENT_MIME_TYPE_BY_EXTENSION)),
];

const normalizePeopleDocumentAsset = (asset) => {
	if (!asset?.uri) {
		throw new Error('Unable to read the selected file');
	}

	const fileName = asset.name || asset.fileName || `document-${Date.now()}`;
	const extension = String(fileName).split('.').pop().toLowerCase();
	const [providedMimeType] = String(asset.mimeType || '').toLowerCase().split(';');
	const mimeType = PEOPLE_DOCUMENT_PICKER_MIME_TYPES.includes(providedMimeType)
		? providedMimeType
		: PEOPLE_DOCUMENT_MIME_TYPE_BY_EXTENSION[extension];

	if (!mimeType) {
		throw new Error('Choose a PDF, Word document or image');
	}

	const sizeBytes = Number(asset.size);
	if (Number.isFinite(sizeBytes) && sizeBytes > PEOPLE_MAX_UPLOAD_BYTES) {
		throw new Error('File must be 10 MB or smaller');
	}

	return {
		...asset,
		fileName,
		mimeType,
	};
};

const buildAuthHeaders = async () => {
	const authToken = await AsyncStorage.getItem('token');
	return { authorization: `JWT ${authToken}` };
};

const logPeopleS3Error = (context, error) => {
	const status = error?.response?.status || null;
	const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';

	// Avoid logging full axios errors because they may include pre-signed URLs in `error.config.url`.
	console.error(`[people] ${context} failed`, { status, message });
};

// Uploads a blob to the employee-scoped people storage and returns its s3Key ('' on failure).
export const peopleS3UploadMobile = async ({
	blob,
	fileName,
	mimeType,
	employeeId,
	getServerEndpoint,
}) => {
	try {
		const headers = await buildAuthHeaders();
		const signResponse = await axios.post(
			`${await getServerEndpoint()}/people/sign_s3_upload`,
			{
				scope: 'employee',
				employeeId,
				fileName,
				mimeType,
				sizeBytes: blob?.size,
			},
			{ headers },
		);

		const { signedRequest, s3Key, uploadHeaders } = signResponse?.data?.data?.returnData || {};
		if (!signedRequest || !s3Key) {
			return '';
		}

		const putResponse = await fetch(signedRequest, {
			method: 'PUT',
			headers: {
				'Content-Type': mimeType,
				...(uploadHeaders || {}),
			},
			body: blob,
		});

		// A failed PUT must not yield an s3Key, or records would point at missing files.
		if (!putResponse.ok) {
			console.error('[people] Employee upload failed', { status: putResponse.status });
			return '';
		}

		return s3Key;
	}
	catch (error) {
		logPeopleS3Error('Employee upload', error);
		return '';
	}
};

// Uploads an in-memory PDF generated by the mobile app to employee-scoped People storage.
export const uploadPeoplePdfBytes = async ({
	pdfBytes,
	fileName,
	employeeId,
	getServerEndpoint,
}) => {
	if (!pdfBytes?.byteLength) {
		return '';
	}

	if (pdfBytes.byteLength > PEOPLE_MAX_UPLOAD_BYTES) {
		throw new Error('Form response must be 10 MB or smaller');
	}

	let temporaryFile;
	let blob;
	try {
		if (Platform.OS === 'web') {
			blob = new Blob([pdfBytes], { type: 'application/pdf' });
		}
		else {
			temporaryFile = new File(Paths.cache, `people-form-response-${Date.now()}.pdf`);
			temporaryFile.write(pdfBytes);
			const fileResponse = await fetch(temporaryFile.uri);
			blob = await fileResponse.blob();
		}

		return await peopleS3UploadMobile({
			blob,
			fileName,
			mimeType: 'application/pdf',
			employeeId,
			getServerEndpoint,
		});
	}
	finally {
		try {
			blob?.close?.();
		}
		catch (error) {
			logPeopleS3Error('Form response memory cleanup', error);
		}
		try {
			if (temporaryFile?.exists) {
				temporaryFile.delete();
			}
		}
		catch (error) {
			logPeopleS3Error('Temporary form response cleanup', error);
		}
	}
};

// Compresses a picked photo asset and uploads it to the employee-scoped people storage.
// Returns the created file descriptor ({ s3Key, fileName, mimeType, sizeBytes }) or null.
export const uploadPeopleImageAsset = async ({
	asset,
	employeeId,
	getServerEndpoint,
}) => {
	if (!asset?.uri) {
		return null;
	}

	const compressedImage = await manipulateAsync(
		asset.uri,
		[{ resize: { height: 1620 } }],
		{ compress: 0.9, format: SaveFormat.JPEG },
	);

	const imageResponse = await fetch(compressedImage.uri);
	const blob = await imageResponse.blob();
	const fileName = asset.fileName || `photo-${Date.now()}.jpg`;
	const mimeType = 'image/jpeg';
	if (blob.size > PEOPLE_MAX_UPLOAD_BYTES) {
		throw new Error('Photo must be 10 MB or smaller');
	}

	const s3Key = await peopleS3UploadMobile({
		blob,
		fileName,
		mimeType,
		employeeId,
		getServerEndpoint,
	});

	if (!s3Key) {
		return null;
	}

	return {
		s3Key,
		fileName,
		mimeType,
		sizeBytes: blob.size,
	};
};

// Uploads a document picker asset without image conversion so PDF and Word files
// retain their original content and supported images retain their original format.
export const uploadPeopleDocumentAsset = async ({
	asset,
	employeeId,
	getServerEndpoint,
}) => {
	const normalizedAsset = normalizePeopleDocumentAsset(asset);
	const fileResponse = await fetch(normalizedAsset.uri);
	const blob = await fileResponse.blob();

	if (blob.size > PEOPLE_MAX_UPLOAD_BYTES) {
		throw new Error('File must be 10 MB or smaller');
	}

	const s3Key = await peopleS3UploadMobile({
		blob,
		fileName: normalizedAsset.fileName,
		mimeType: normalizedAsset.mimeType,
		employeeId,
		getServerEndpoint,
	});

	if (!s3Key) {
		return null;
	}

	return {
		s3Key,
		fileName: normalizedAsset.fileName,
		mimeType: normalizedAsset.mimeType,
		sizeBytes: blob.size,
	};
};

// Uploads a PNG data URL (e.g. a drawn signature) to the employee-scoped people storage.
// Returns the s3Key ('' on failure).
export const uploadPeoplePngDataUrl = async ({
	dataUrl,
	fileName,
	employeeId,
	getServerEndpoint,
}) => {
	if (!dataUrl) {
		return '';
	}

	// manipulateAsync materialises the data URL into an uploadable local file on native;
	// on web it returns a data/blob URI that fetch can read directly.
	const pngImage = await manipulateAsync(dataUrl, [], { format: SaveFormat.PNG });
	const pngResponse = await fetch(pngImage.uri);
	const blob = await pngResponse.blob();

	return peopleS3UploadMobile({
		blob,
		fileName,
		mimeType: 'image/png',
		employeeId,
		getServerEndpoint,
	});
};

// Returns a short-lived signed download URL for a people file ('' on failure).
export const getPeopleSignedDownloadUrlMobile = async ({
	scope = 'employee',
	employeeId,
	s3Key,
	fileName,
	getServerEndpoint,
}) => {
	try {
		const headers = await buildAuthHeaders();
		const response = await axios.post(
			`${await getServerEndpoint()}/people/sign_s3_download`,
			{
				scope,
				employeeId,
				s3Key,
				fileName,
			},
			{ headers },
		);

		return response?.data?.data?.returnData?.signedRequest || '';
	}
	catch (error) {
		logPeopleS3Error('Signed download URL', error);
		return '';
	}
};
